import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';

/** Series configuration for area charts */
export interface AreaSeries {
  dataKey: string;
  name: string;
  color: string;
  fillOpacity?: number;
}

export interface AreaChartWrapperProps {
  /** Chart data array */
  data: Record<string, unknown>[];
  /** X-axis data key */
  xAxisKey: string;
  /** Area series definitions */
  series: AreaSeries[];
  /** Chart height in pixels */
  height?: number;
  /** Y-axis label */
  yAxisLabel?: string;
  /** Y-axis domain */
  yDomain?: [number | 'auto', number | 'auto'];
  /** Custom tooltip formatter */
  tooltipFormatter?: (value: ValueType | undefined, name: NameType | undefined) => [React.ReactNode, NameType] | React.ReactNode;
}

const GRID_COLOR = '#EFEFEF';
const AXIS_TICK_COLOR = '#555555';

/**
 * Recharts AreaChart wrapper with standard OUTFRONT styling.
 */
export function AreaChartWrapper({
  data,
  xAxisKey,
  series,
  height = 300,
  yAxisLabel,
  yDomain,
  tooltipFormatter,
}: AreaChartWrapperProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <defs>
          {series.map((s) => (
            <linearGradient key={`gradient-${s.dataKey}`} id={`gradient-${s.dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={s.color} stopOpacity={s.fillOpacity ?? 0.3} />
              <stop offset="95%" stopColor={s.color} stopOpacity={0.05} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
        <XAxis
          dataKey={xAxisKey}
          tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
          tickLine={false}
          axisLine={{ stroke: GRID_COLOR }}
        />
        <YAxis
          tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
          tickLine={false}
          axisLine={false}
          domain={yDomain}
          label={
            yAxisLabel
              ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 12, fill: AXIS_TICK_COLOR }
              : undefined
          }
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #DCDCDC',
            borderRadius: 4,
            fontSize: 13,
          }}
          formatter={tooltipFormatter}
        />
        <Legend
          verticalAlign="top"
          iconType="circle"
          wrapperStyle={{ paddingBottom: 16, fontSize: 11, fontWeight: 600 }}
        />
        {series.map((s) => (
          <Area
            key={s.dataKey}
            type="monotone"
            dataKey={s.dataKey}
            name={s.name}
            stroke={s.color}
            strokeWidth={2}
            fill={`url(#gradient-${s.dataKey})`}
            dot={{ r: 4, fill: s.color }}
            activeDot={{ r: 6 }}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default AreaChartWrapper;
